import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The shared OAuth-popup state machine behind every company-census "connect
 * your HR system" card (QuickBooks Payroll, BambooHR).
 *
 * Extracted from the original `use-census-quickbooks` hook rather than copied:
 * the dual-trigger resolution below is subtle enough that a second hand-written
 * copy would be the likeliest place for a second provider to ship a bug. The
 * QuickBooks tests moved with it and are the regression guard.
 *
 * Flow:
 *
 *   startConnect(arg?)
 *     → authorize(arg)  (mint the provider's OAuth URL for the caller's draft)
 *     → open the consent popup, poll for close
 *   provider → apps/api GET .../callback (exchange + pull + persist)
 *     → redirects the popup to /oauth/{provider}/callback?oauth=success
 *     → that page postMessages the opener (handled below)
 *   on success → getResult() (mapped roster + business)
 *     → onConnected(result)
 *
 * The popup callback page posts `{ type: messageType, status }`.
 *
 * The popup is also kept in FRONT of the census tab for as long as it's live —
 * see `raisePopup` below for why that isn't cosmetic.
 *
 * Both triggers depend on the popup coming back to a page WE serve. When it
 * doesn't — apps/api's callback overran API Gateway's timeout and the popup was
 * left sitting on a raw gateway 504 — neither fires: no postMessage, and the
 * page never closes itself. `step` stays "connecting", which keeps `isBusy`
 * true, which locks both provider cards and Back. The employer is stuck on a
 * spinner with no way forward and no way out but a reload. That is what
 * `POPUP_WATCHDOG_MS` below exists to prevent.
 */

/**
 * Last-resort ceiling on "connecting".
 *
 * Deliberately generous: the popup is user-paced (provider login, MFA, picking
 * a company), so this must not fire on someone simply taking their time. It is
 * a backstop for a wedged popup, NOT the fix for a slow callback — the callback
 * is bounded server-side by its own pull budget so that it always redirects.
 */
const POPUP_WATCHDOG_MS = 4 * 60_000;

export type CensusConnectStep =
  | "idle"
  | "authorizing"
  | "connecting"
  | "fetching"
  | "done"
  | "error";

/** Everything a connected result must expose for the machine to judge success. */
interface ConnectResultLike {
  connected: boolean;
}

interface UseCensusHrConnectOptions<TResult extends ConnectResultLike, TArg> {
  /** Message `type` the provider's popup callback page posts. */
  messageType: string;
  /** Mint the provider's authorize URL. */
  authorize: (arg: TArg) => Promise<{ authorizeUrl: string }>;
  /** Read the persisted pull once consent completes. */
  getResult: () => Promise<TResult>;
  /** Called once the pull has been fetched and mapped. */
  onConnected: (result: TResult) => void;
  /** Fired on each state transition — used to emit analytics events. */
  onStep?: (step: CensusConnectStep, meta?: { error?: string; result?: TResult }) => void;
  /** Copy for the failure modes the machine words itself. */
  messages: {
    /** Popup reported success but nothing was persisted. */
    notConnected: string;
    /** `getResult` threw. */
    resultFailed: string;
    /** `authorize` threw. */
    authorizeFailed: string;
    /** The popup never came back — see POPUP_WATCHDOG_MS. */
    timedOut: string;
  };
}

export interface UseCensusHrConnectReturn<TArg> {
  step: CensusConnectStep;
  error: string | null;
  isBusy: boolean;
  startConnect: (arg: TArg) => Promise<void>;
  reset: () => void;
}

export function useCensusHrConnect<TResult extends ConnectResultLike, TArg = void>(
  opts: UseCensusHrConnectOptions<TResult, TArg>,
): UseCensusHrConnectReturn<TArg> {
  const [step, setStep] = useState<CensusConnectStep>("idle");
  const [error, setError] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep the latest callbacks in refs so the message listener (registered once)
  // never runs a stale closure.
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const goStep = useCallback(
    (next: CensusConnectStep, meta?: { error?: string; result?: TResult }) => {
      setStep(next);
      optsRef.current.onStep?.(next, meta);
    },
    [],
  );

  const clearPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
  }, []);

  // Guards `resolve` so the two triggers (postMessage + popup-close poll) can't
  // both run the fetch. Once we reach a terminal state we don't resolve again.
  const resolvingRef = useRef(false);

  /**
   * Raise the consent popup above the census tab.
   *
   * Not cosmetic: while the popup is live both provider cards and the Back
   * control are locked (`isBusy`), and ONLY the popup can advance the flow. If it
   * sinks behind the app — one click back on the census tab does it — the user
   * sees a spinner with no way forward and no window to find.
   *
   * `focus()` is one of the few methods an opener may call on a cross-origin
   * window, but the raise is still best-effort: a browser may decline it
   * (Firefox's `dom.disable_window_flip`), and a window handle can throw once the
   * popup is being torn down. Never let that surface to the caller.
   */
  const raisePopup = useCallback((): boolean => {
    const popup = popupRef.current;
    // Skip once resolving: the callback page closes itself ~1.2s after posting,
    // and yanking the user into a window that's about to vanish reads as a jump.
    if (!popup || popup.closed || resolvingRef.current) return false;
    try {
      popup.focus();
      return true;
    } catch {
      return false;
    }
  }, []);

  /**
   * Single, idempotent resolution path. Reached EITHER when the popup posts its
   * result OR — the reliable trigger — when the popup simply closes. We don't
   * depend on postMessage delivery (it can be dropped across domains / by the
   * browser): by the time the popup closes, apps/api's callback has already
   * exchanged, pulled, and persisted, so we just read the server's result.
   *
   * `trigger` distinguishes an explicit popup error from a plain close so a
   * cancel (close with nothing persisted) returns to idle instead of erroring.
   * `"timeout"` is the watchdog: it still reads the result first, because a
   * wedged popup does not mean the server-side pull failed — if the data landed
   * we complete the flow normally instead of telling the employer to redo it.
   */
  const resolve = useCallback(
    async (trigger: "success" | "close" | "timeout", popupError?: string) => {
      if (resolvingRef.current) return;
      resolvingRef.current = true;
      clearPoll();

      if (popupError) {
        setError(popupError);
        goStep("error", { error: popupError });
        resolvingRef.current = false;
        return;
      }

      goStep("fetching");
      try {
        const result = await optsRef.current.getResult();
        if (result.connected) {
          goStep("done", { result });
          optsRef.current.onConnected(result);
        } else if (trigger === "timeout") {
          // The popup never came back AND nothing was persisted. Unlock the step
          // and say so — the alternative is the indefinite spinner this watchdog
          // exists to kill.
          const message = optsRef.current.messages.timedOut;
          setError(message);
          goStep("error", { error: "popup_timeout" });
          resolvingRef.current = false;
        } else if (trigger === "success") {
          // The popup reported success but nothing was persisted — a real
          // server-side failure (e.g. the pull/persist errored). Surface it.
          const message = optsRef.current.messages.notConnected;
          setError(message);
          goStep("error", { error: "not_connected" });
          resolvingRef.current = false;
        } else {
          // Popup closed with no data — treat as a cancel; let them retry.
          goStep("idle");
          resolvingRef.current = false;
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : optsRef.current.messages.resultFailed;
        setError(message);
        goStep("error", { error: message });
        resolvingRef.current = false;
      }
    },
    [goStep, clearPoll],
  );

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.data?.type !== optsRef.current.messageType) return;
      // Fast path only — the popup-close poll is the reliable trigger, so we
      // don't gate on origin/source here (the payload carries no secrets and a
      // stale/foreign message at worst re-reads the caller's own result).
      if (event.data.status === "success") void resolve("success");
      else void resolve("success", event.data.error || "Authorization failed.");
    }
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      clearPoll();
    };
  }, [resolve, clearPoll]);

  // Keep the popup on top for as long as it's live. The opener regaining focus
  // is exactly the moment the popup got buried, so re-raise it there. Tab
  // switches don't fire `focus` in every browser, hence the visibility listener
  // too — `raisePopup` no-ops when there's nothing live, so double-firing is
  // free. There's no focus fight: raising the popup blurs the opener, and this
  // only ever runs when the opener gains focus.
  useEffect(() => {
    const onOpenerFocus = () => {
      raisePopup();
    };
    const onVisibility = () => {
      if (!document.hidden) raisePopup();
    };
    window.addEventListener("focus", onOpenerFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onOpenerFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [raisePopup]);

  const startConnect = useCallback(
    async (arg: TArg) => {
      setError(null);
      resolvingRef.current = false;
      goStep("authorizing");
      try {
        const { authorizeUrl } = await optsRef.current.authorize(arg);

        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        popupRef.current = window.open(
          authorizeUrl,
          `${optsRef.current.messageType}-popup`,
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`,
        );

        if (!popupRef.current) {
          setError("Popup blocked. Please allow popups for this site and try again.");
          goStep("error", { error: "popup_blocked" });
          return;
        }

        goStep("connecting");
        // Some browsers open a popup behind the opener (or the opener steals
        // focus back as `startConnect` settles) — raise it explicitly.
        raisePopup();
        pollRef.current = setInterval(() => {
          if (popupRef.current?.closed) {
            // Reliable trigger: the popup closed, so the server-side callback
            // has finished — read the result (works even if postMessage was
            // dropped).
            void resolve("close");
          }
        }, 500);

        // Backstop for a popup that neither posts nor closes. Closing it first
        // is deliberate: we're about to tell the employer to retry, and a stale
        // window parked on a provider error page is only in the way.
        watchdogRef.current = setTimeout(() => {
          try {
            popupRef.current?.close();
          } catch {
            // A handle mid-teardown can throw; the watchdog must still run.
          }
          void resolve("timeout");
        }, POPUP_WATCHDOG_MS);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : optsRef.current.messages.authorizeFailed;
        setError(message);
        goStep("error", { error: message });
      }
    },
    [goStep, resolve, raisePopup],
  );

  const reset = useCallback(() => {
    clearPoll();
    resolvingRef.current = false;
    popupRef.current = null;
    setError(null);
    setStep("idle");
  }, [clearPoll]);

  return {
    step,
    error,
    isBusy: step === "authorizing" || step === "connecting" || step === "fetching",
    startConnect,
    reset,
  };
}
